import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import { useNavigate, useSearchParams } from 'react-router-dom';
import sessionCheck from "../session/userData";
import { encryptDataHybrid } from "../enc/useEncrypt";
import { getDeviceInfo } from '../utils/deviceInfo';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Papa from 'papaparse';
import {
    DocumentArrowDownIcon,
    ChevronDownIcon,
    ChevronUpIcon,
    MagnifyingGlassIcon,
    ArrowLeftIcon,
    ChevronLeftIcon,
    XMarkIcon
} from '@heroicons/react/24/outline';
import { TbFilterPause } from "react-icons/tb";
import { motion, AnimatePresence } from 'framer-motion';

const API_URL = process.env.REACT_APP_API_URL;
const APP_NAME = process.env.REACT_APP_NAME;
const TRADE_NAME = process.env.REACT_APP_TRADE_NAME;
const CONTACT_EMAIL = process.env.REACT_APP_CONTACT_EMAIL;
const CONTACT_NUMBER = process.env.REACT_APP_CONTACT_NUMBER;
const CONTACT_ADDRESS = process.env.REACT_APP_CONTACT_ADDRESS;
const GST_NUMBER = process.env.REACT_APP_GST_NUMBER;
const WEBSITE = process.env.REACT_APP_WEB_URL;

// Skeleton Loading Components
const TableSkeleton = () => (
    <div className="animate-pulse">
        <div className="h-8 bg-gray-200 dark:bg-gray-600 rounded mb-4"></div>
        {[...Array(5)].map((_, i) => (
            <div key={i} className="h-12 bg-gray-100 dark:bg-gray-700 rounded mb-2"></div>
        ))}
    </div>
);

const Ledger = () => {
    const [searchParams] = useSearchParams();
    const userId = searchParams.get('userId');
    const [loading, setLoading] = useState(false);
    const [ledgerData, setLedgerData] = useState([]);
    const [access_data, setAccess_data] = useState(null);
    const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
    const navigate = useNavigate();

    // Get today's date in YYYY-MM-DD format
    const getTodayDate = () => {
        const today = new Date();
        return today.toISOString().split('T')[0];
    };

    const [filters, setFilters] = useState({
        userId: userId || '',
        fromDate: getTodayDate(),
        toDate: getTodayDate()
    });

    useEffect(() => {
        if (sessionCheck() == null) {
            navigate("../login", { replace: true });
        } else {
            setAccess_data(JSON.parse(sessionCheck()));
        }
    }, []);

    useEffect(() => {
        if (access_data?.username) {
            fetchLedgerData();
        }
    }, [access_data?.username]);

    useEffect(() => {
        if (filterDrawerOpen) {
            // disable scrolling
            document.body.style.overflow = "hidden";
        } else {
            // enable scrolling again
            document.body.style.overflow = "auto";
        }

        // cleanup when component unmounts
        return () => {
            document.body.style.overflow = "auto";
        };
    }, [filterDrawerOpen]);

    const fetchLedgerData = async () => {
        setLoading(true);
        try {
            const dataToEncrypt = {
                user_id: access_data?.username || '',
                login_id: access_data?.login_id || '',
                device_id: getDeviceInfo().deviceId,
                payload: {
                    user_id: filters.userId,
                    from_date: filters.fromDate,
                    to_date: filters.toDate
                }
            };

            const encryptedMessage = encryptDataHybrid(dataToEncrypt);

            if (!encryptedMessage) {
                toast.error("Encryption failed. Please try again.");
                setLoading(false);
                return;
            }

            const response = await fetch(`${API_URL}ledger.php`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${Math.floor(Date.now() / 1000) * 784116}`,
                },
                body: JSON.stringify({
                    encrypted_key: encryptedMessage.encryptedKey,
                    encrypted_data: encryptedMessage.encryptedData,
                }),
            });

            const result = await response.json();

            if (result.status === true) {
                setLedgerData(result.data || []);
            } else {
                toast.error(result.message || "Failed to fetch ledger data.");
                setLedgerData([]);
            }
        } catch (error) {
            console.error("Fetch ledger error:", error);
            toast.error("An unexpected error occurred. Please try again.");
            setLedgerData([]);
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        fetchLedgerData();
        setFilterDrawerOpen(false);
    };

    const resetFilters = () => {
        const today = getTodayDate();
        setFilters({
            userId: userId || '',
            fromDate: today,
            toDate: today
        });
        setFilterDrawerOpen(false);
    };

    const handleDownload = () => {
        if (ledgerData.length === 0) {
            toast.warning("No data available to download");
            return;
        }

        // Prepare company and user details
        const companyDetails = [
            ["Company Name", `${TRADE_NAME}`],
            ["Application", `${APP_NAME}`],
            ["GST", `${GST_NUMBER || ''}`],
            ["Address", `${CONTACT_ADDRESS}`],
            ["Phone", `${CONTACT_NUMBER}`],
            ["Email", `${CONTACT_EMAIL}`],
            ["Website", `${WEBSITE}`],
            ["", ""]
        ];

        const userDetails = [
            ["User ID", userId || ''],
            ["From Date", filters.fromDate],
            ["To Date", filters.toDate],
            ["Download Date", new Date().toLocaleString()],
            ["", ""],
            ["", ""],
        ];

        // Prepare the ledger data
        const ledgerHeaders = [
            "Date",
            "Order ID",
            "Purpose",
            "Description",
            "Debit",
            "Credit",
            "Balance"
        ];

        const ledgerRows = ledgerData.map(entry => [
            entry.createAt,
            entry.order_id,
            `${getCategory(entry.category)} | ${entry.number}`,
            entry.description,
            entry.dr_amount !== "0" ? entry.dr_amount : '-',
            entry.cr_amount !== "0" ? entry.cr_amount : '-',
            entry.balance
        ]);

        // Combine all data
        const csvData = [
            ...companyDetails,
            ...userDetails,
            ledgerHeaders,
            ...ledgerRows
        ];

        // Convert to CSV
        const csv = Papa.unparse({
            data: csvData,
            quotes: true,
            header: false
        });

        // Create a blob and download
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `ledger_${filters.fromDate}_to_${filters.toDate}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        toast.success("Download started successfully");
    };

    const getCategory = (category) => {
        switch (category) {
            case "1":
                return "Add Money";
            case "2":
                return "Mobile Recharge";
            case "3":
                return "Bill Payment";
            case "4":
                return "Bonus";
            case "5":
                return "Debit Money";
            case "6":
                return "Fund Request";
            case "7":
                return "Credit from Admin";
            case "8":
                return "Debit Adjustment";
            case "9":
                return "Credit Transfer";
            case "10":
                return "Debit Transfer";
            case "11":
                return "Payout to Bank Account";
            case "12":
                return "Settlement to Wallet";
            case "13":
                return "Credit Adjustment";
            case "14":
                return "SMS Charges";
            default:
                return undefined;
        }
    }

    return (
        <div className="min-h-screen bg-gray-50 text-gray-900 dark:bg-gray-900 dark:text-white">
            <div className="flex flex-col flex-1">
                <Header />
                <main className="flex-1 overflow-auto">
                    {/* Filter Drawer */}
                    <AnimatePresence>
                        {filterDrawerOpen && (
                            <motion.div
                                initial={{ x: '-100%' }}
                                animate={{ x: 0 }}
                                exit={{ x: '-100%' }}
                                transition={{ type: 'tween' }}
                                className="fixed inset-y-0 left-0 z-50 w-80 bg-white dark:bg-gray-800 shadow-lg overflow-y-auto"
                            >
                                <div className="p-4">
                                    <div className="flex items-center justify-between mb-4">
                                        <h2 className="text-lg font-medium">Filter Ledger</h2>
                                        <button
                                            onClick={() => setFilterDrawerOpen(false)}
                                            className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700"
                                        >
                                            <XMarkIcon className="h-5 w-5 text-gray-600 dark:text-gray-300" />
                                        </button>
                                    </div>

                                    <form onSubmit={handleSubmit} className="space-y-4">
                                        <div className="space-y-1">
                                            <label htmlFor="userId" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                                User ID
                                            </label>
                                            <input
                                                type="text"
                                                name="userId"
                                                id="userId"
                                                value={filters.userId}
                                                onChange={handleFilterChange}
                                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm bg-white dark:bg-gray-700"
                                                placeholder="Enter user ID"
                                            />
                                        </div>

                                        <div className="space-y-1">
                                            <label htmlFor="fromDate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                                From Date
                                            </label>
                                            <input
                                                type="date"
                                                name="fromDate"
                                                id="fromDate"
                                                value={filters.fromDate}
                                                onChange={handleFilterChange}
                                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm bg-white dark:bg-gray-700"
                                            />
                                        </div>

                                        <div className="space-y-1">
                                            <label htmlFor="toDate" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                                To Date
                                            </label>
                                            <input
                                                type="date"
                                                name="toDate"
                                                id="toDate"
                                                value={filters.toDate}
                                                onChange={handleFilterChange}
                                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm bg-white dark:bg-gray-700"
                                            />
                                        </div>

                                        <div className="mt-6 flex space-x-3">
                                            <button
                                                type="button"
                                                onClick={resetFilters}
                                                className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-gray-200 rounded-md hover:bg-gray-300 dark:hover:bg-gray-600"
                                            >
                                                Reset
                                            </button>
                                            <button
                                                type="submit"
                                                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                                            >
                                                Apply Filters
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>

                    {/* Overlay when filter drawer is open */}
                    {filterDrawerOpen && (
                        <div
                            className="fixed inset-0 bg-black bg-opacity-50 z-40"
                            onClick={() => setFilterDrawerOpen(false)}
                        />
                    )}
                </main>
                <main className="flex-1 p-6 overflow-auto">
                    <div className="max-w-7xl mx-auto">
                        <div className="flex items-center justify-between mb-6">
                            <button
                                onClick={() => navigate(-1)}
                                className="flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-600"
                            >
                                <ChevronLeftIcon className="h-5 w-5 mr-1" />
                                Back
                            </button>
                            <h2 className="text-xl font-semibold">
                                User Ledger
                            </h2>
                            <button
                                onClick={() => setFilterDrawerOpen(true)}
                                className="p-2 rounded-full hover:bg-gray-300 bg-gray-200 dark:hover:bg-gray-700 flex items-center justify-center"
                            >
                                <TbFilterPause className="h-5 w-5 text-gray-600 dark:text-gray-300" />
                            </button>
                        </div>
                        {/* Actions Bar */}
                        <div className="flex justify-between items-center mb-4">
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                                Showing {ledgerData.length} records
                            </div>
                            <button
                                onClick={handleDownload}
                                disabled={ledgerData.length === 0}
                                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <DocumentArrowDownIcon className="h-5 w-5 mr-2" />
                                Download
                            </button>
                        </div>

                        {/* Ledger Table */}
                        <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
                            {loading ? (
                                <div className="p-6">
                                    <TableSkeleton />
                                </div>
                            ) : ledgerData.length > 0 ? (
                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-600">
                                        <thead className="bg-gray-50 dark:bg-gray-700">
                                            <tr>
                                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                                    Date
                                                </th>
                                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                                    Order ID
                                                </th>
                                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                                    Purpose
                                                </th>
                                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                                    Description
                                                </th>
                                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                                    Debit
                                                </th>
                                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                                    Credit
                                                </th>
                                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                                    Balance
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-600">
                                            {ledgerData.map((entry, index) => (
                                                <tr key={index} className={index % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-700'}>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                                        {entry.createAt}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                                        {entry.order_id}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                                        {getCategory(entry.category)} | {entry.number}
                                                    </td>
                                                    <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">
                                                        {entry.description}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600 dark:text-red-400">
                                                        {entry.dr_amount !== "0" ? entry.dr_amount : '-'}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600 dark:text-green-400">
                                                        {entry.cr_amount !== "0" ? entry.cr_amount : '-'}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                                                        {entry.balance}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            ) : (
                                <div className="p-6 text-center">
                                    <p className="text-gray-500 dark:text-gray-400">
                                        No ledger data found. Apply filters to view records.
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                </main>
            </div>
            <ToastContainer />
        </div>
    );
};

export default Ledger;